import React, {useEffect} from 'react';
import {DialogTypes} from 'common/names';
import DialogComponent from 'components/Dialog';
import {useDispatch, useSelector} from 'react-redux';
import {Trans, useTranslation} from 'react-i18next';
import {IJobSettings, JobSettings} from 'types/JobSettings';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import AsyncContainer from 'components/AsyncContainer';
import Box from '@mui/material/Box';
import Panel from 'components/Panel';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import {settingsActions, settingsSelectors} from 'redux/settings';
import FormikNumberField from 'components/inputs/FormikNumberField';
import InputAdornment from '@mui/material/InputAdornment';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';

type Props = {
   onClose: () => void;
};

const JobQueueSettingsDialog = ({onClose}: Props) => {
   const {t} = useTranslation();
   const jobSettings = useSelector(settingsSelectors.getJobSettings);
   const isFetching = useSelector(settingsSelectors.isFetching);
   const dispatch = useDispatch();

   useEffect(() => {
      dispatch(settingsActions.getJobSettings);
   }, [dispatch]);

   const saveSettings = (values: IJobSettings, helpers: FormOnSubmitHelpers<IJobSettings>) => {
      dispatch(
         settingsActions.postJobSettings(values, {
            onSuccess: onClose,
            onError: (errors: any) => {
               helpers.setSubmitting(false);
               helpers.setErrors(errors);
            },
         })
      );
   };

   return (
      <DialogComponent
         maxWidth="xs"
         header={DialogTypes.JobQueueSettingsDialog.dialogName}
         close={onClose}
      >
         <AsyncContainer loading={isFetching}>
            <Formik
               initialValues={JobSettings.cast(jobSettings) as IJobSettings}
               validationSchema={JobSettings}
               onSubmit={saveSettings}
               enableReinitialize
            >
               <>
                  <Panel>
                     <FormikNumberField
                        autoFocus
                        name="startupDelay"
                        label={t('Delay between initial job starts')}
                        InputProps={{
                           endAdornment: (
                              <InputAdornment position={'end'}>{t('Milliseconds')}</InputAdornment>
                           ),
                        }}
                     />
                     <FormikNumberField
                        name="numConcurrentJobs"
                        label={t('Maximum number of concurrently running jobs')}
                     />
                     <Trans t={t}>
                        <strong>NOTE:</strong> Startup delay changes take effect on service restart
                        and do not affect daily or monthly job schedules.
                     </Trans>
                  </Panel>
                  <LastUpdatedLabel date={jobSettings.timestamp} author={jobSettings.lastUser} />
                  <Box textAlign={'center'}>
                     <FormFooterButtons cancel={onClose} submitLabel={t('Save')} />
                  </Box>
               </>
            </Formik>
         </AsyncContainer>
      </DialogComponent>
   );
};

export default React.memo(JobQueueSettingsDialog);
